import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";

import CustomerListComp from "../components/customer/customerList.comp";
import CustomerListFilterComp from "../components/customer/customerListFilter.comp";
import Layout from "../components/layouts/layout.comp";
import Error from "../components/reuseables/error.comp";
import { loadCustomers } from "../core/customer.core";
import { useTranslation } from "../core/translation.core";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import { CustomerListFilter } from "../types/customer";
import { post } from "../core/fetch.core";
import OutlineBtn from "../components/form/outlineBtn.comp";
import PageNav from "../components/common/pageNav.comp";
import membershipCore from "../core/membership.core";
import AddLink from "../components/form/addLink.comp";

export default function CustomersPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.customer.customers)} titleBtn={<TitleBtns />}>
      <div>
        <Error />
        <Content />
      </div>
    </Layout>
  );
}

function Content() {
  const { query, isReady } = useRouter();
  const methods = useForm<CustomerListFilter>({
    defaultValues: {
      page: 1,
      pageSize: 50,
      sort: "createdAt",
      sortAsec: -1,
    },
  });

  const {
    data,
    isPending,
    isError: isError2,
    error: error2,
  } = useQuery({
    queryKey: ["refreshMembership"],
    queryFn: () => membershipCore.refreshShopMembership(),
    enabled: isReady,
    refetchOnWindowFocus: false,
  });

  // sync the page param back to form hook
  useEffect(() => {
    methods.setValue("page", Number(query.page));
    // eslint-disable-next-line
  }, [query, isReady]);

  return (
    <>
      <CustomerListFilterComp methods={methods} />
      <Main methods={methods} />
    </>
  );
}

function Main({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { query } = useRouter();

  const [
    pageSize,
    search,
    sort,
    sortAsec,
    providers,
    emailVerified,
    mobileNumberVerified,
    suspended,
    blacklisted,
    dateFrom,
    dateTo,
    membershipIds,
  ] = useWatch({
    name: [
      "pageSize",
      "search",
      "sort",
      "sortAsec",
      "providers",
      "emailVerified",
      "mobileNumberVerified",
      "suspended",
      "blacklisted",
      "dateFrom",
      "dateTo",
      "membershipIds",
    ],
    control: methods.control,
  });

  const {
    data: customers,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: [
      "customers",
      query.page,
      pageSize,
      search,
      sort,
      sortAsec,
      providers,
      emailVerified,
      mobileNumberVerified,
      suspended,
      blacklisted,
      dateFrom,
      dateTo,
      membershipIds,
    ],
    queryFn: (context) =>
      loadCustomers({
        paging: {
          page: context.queryKey[1] as number,
          size: context.queryKey[2] as number,
        },
        search: context.queryKey[3] as string,
        sort: context.queryKey[4] as string,
        sortAsec: context.queryKey[5] as number,
        providers: context.queryKey[6] as string[],
        emailVerified: context.queryKey[7] as boolean,
        mobileNumberVerified: context.queryKey[8] as boolean,
        suspended: context.queryKey[9] as boolean,
        blacklisted: context.queryKey[10] as boolean,
        dateFrom: context.queryKey[11] as string,
        dateTo: context.queryKey[12] as string,
        membershipIds: context.queryKey[13] as string[],
      }),
  });

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading) return <LoadingOvalCenterIcon />;

  return (
    <section data-type="section" className="mt-4">
      <CustomerListComp customers={customers!} methods={methods} />
      <Paging methods={methods} total={customers ? customers.size : 0} />
    </section>
  );
}

function TitleBtns() {
  const queryClient = useQueryClient();

  async function createDummyCustomers() {
    await post("/test/create10000Customers", {});
    queryClient.invalidateQueries({ queryKey: ["customers"] });
  }

  return (
    <div className="flex gap-2 items-center">
      <AddLink id="customer-create" href="/customers/add" cap="manageCustomer" />
      {process.env.NODE_ENV === "development" && (
        <OutlineBtn id="create-dummy-customers" onClick={createDummyCustomers}>
          Create Dummy Customers
        </OutlineBtn>
      )}
    </div>
  );
}

function Paging({ methods, total }: { methods: UseFormReturn<CustomerListFilter, any>; total: number }) {
  const router = useRouter();
  const [pageSize] = useWatch({
    name: ["pageSize"],
    control: methods.control,
  });

  return (
    <PageNav page={Number(router.query.page) || 1} size={pageSize} total={total} pathname="/customers" query={router.query} />
  );
}
